import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';

import './styles.scss';

export default function TruncableText({
    text,
    maxLines = 2,
    component: Component = 'p',
    className = '',
    style = {}
}) {
    const { t } = useTranslation('fragments');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const [showButton, setShowButton] = useState(false);
    const textRef = useRef(null);
    const measureRef = useRef(null);
    const wasTruncatedRef = useRef(false);

    useEffect(() => {
        const checkTruncation = () => {
            if (measureRef.current && textRef.current && text) {
                const measureElement = measureRef.current;
                const fullHeight = measureElement.scrollHeight;

                // Cria um elemento temporário com truncamento para medir a altura truncada
                const tempElement = measureElement.cloneNode(true);
                tempElement.classList.remove('truncable-text-measure');
                tempElement.classList.add('truncable-text-truncated');
                tempElement.style.setProperty('--max-lines', maxLines);
                tempElement.style.position = 'absolute';
                tempElement.style.visibility = 'hidden';
                tempElement.style.top = '-9999px';
                tempElement.style.width = measureElement.offsetWidth + 'px';
                measureElement.parentElement.appendChild(tempElement);

                // Força o reflow
                void tempElement.offsetHeight;

                const truncatedHeight = tempElement.clientHeight;
                tempElement.remove();

                // Verifica se há diferença significativa
                const isTextTruncated = fullHeight > truncatedHeight + 2;
                wasTruncatedRef.current = isTextTruncated;
                setIsTruncated(isTextTruncated);
            }
        };

        const timeoutId = setTimeout(checkTruncation, 100);
        window.addEventListener('resize', checkTruncation);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', checkTruncation);
        };
    }, [text, maxLines, isExpanded]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const handleMouseEnter = () => {
        if (wasTruncatedRef.current && !isExpanded) {
            setShowButton(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isExpanded) {
            setShowButton(false);
        }
    };

    return (
        <div
            className={`truncable-text-container ${wasTruncatedRef.current ? 'truncable-text-container--clickable' : ''}`.trim()}
            style={style}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Component
                ref={textRef}
                className={`truncable-text ${isExpanded ? 'truncable-text-expanded' : 'truncable-text-truncated'} ${wasTruncatedRef.current ? 'truncable-text-clickable' : ''} ${className}`}
                style={{ '--max-lines': maxLines }}
                onClick={wasTruncatedRef.current ? toggleExpanded : undefined}
            >
                {text}
            </Component>
            {/* Elemento invisível para medir a altura completa do texto */}
            <Component
                ref={measureRef}
                className={`truncable-text truncable-text-measure ${className}`}
            >
                {text}
            </Component>
            {wasTruncatedRef.current && !isExpanded && showButton && (
                <button
                    className="truncable-text-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded();
                    }}
                >
                    <ArrowDropDownRoundedIcon className={`truncable-text-icon ${isExpanded ? 'truncable-text-icon-rotated' : ''}`} />
                    {t('text.showAll')}
                </button>
            )}
            {wasTruncatedRef.current && isExpanded && (
                <button
                    className="truncable-text-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleExpanded();
                    }}
                >
                    <ArrowDropDownRoundedIcon className={`truncable-text-icon truncable-text-icon-rotated`} />
                    {t('text.showLess')}
                </button>
            )}
        </div>
    );
}